import { useClient, useItemContext, useQuery } from '@weenat/client'
import { HistoryState, getDefaultHistoryState } from '@weenat/client/dist/HistoryContext'
import { isVirtualWeatherStation } from '@weenat/client/dist/core/devices'
import {
  AllowedMetricsInHistory,
  PredefinedPeriodIds,
  getMetricsToFetch
} from '@weenat/client/dist/core/history/history'
import { UseMetricSummaryChartInterface } from '@weenat/client/dist/core/metrics/useMetricSummaryChart'
import { BillingStatus } from '@weenat/client/dist/enums'
import { TimeSteps } from '@weenat/client/dist/enums/TimeSteps'
import { useIntl } from '@weenat/wintl'
import { useOrgContext } from 'app/orgProvider'
import { usePathname } from 'app/routx-router'
import DumbMetricSummaryCondensed from 'app/src/dashboard/components/common/summary/dailyWeather/DumbMetricSummaryCondensed'
import PeriodBasedMetricSummary from 'app/src/dashboard/components/common/summary/dailyWeather/PeriodBasedMetricSummary'
import { PREVENT_DRAG_CLASSNAME } from 'app/src/dashboard/components/plots/dashboards/constants'
import { useIsAppAccessRestricted } from 'app/src/hooks/useIsAppAccessRestricted'
import { useGlobalHistoryState } from 'app/state'
import { isArray, isNil } from 'lodash-es'
import moment from 'moment-timezone'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMeasure } from 'react-use'
import { styled } from 'styled-components'
import DelimitedFlex from '../DelimitedFlex'
import Icons from '../Icons'
import Text from '../Text'
import { MetricChart } from '../charts/metrics/MetricSummaryChart'
import { SelectFieldPrimitive } from '../fields/SelectField'
import MetricIcons from '../icons/MetricIcons'
import LoadingCircle from '../loaders/LoadingCircle'
import WeedgetCard from './WeedgetCard'
import WeedgetFooter from './WeedgetFooter'
import WeedgetHeader from './WeedgetHeader'
import WeedgetLoading from './WeedgetLoading'

interface MetricProps {
  metric: AllowedMetricsInHistory
}

type OptionValues = 'D' | 'DD' | '7D'
export interface Period {
  key: OptionValues
  since: number
  before: number
  span: TimeSteps
}

export const WeedgetHeaderSelect: typeof SelectFieldPrimitive = styled(SelectFieldPrimitive)`
  margin: ${(p) => p.theme.spacings.none};
  & > div {
    border-width: ${(p) => p.theme.borderWidths.sm}px;
    height: 30px;
    padding-right: 0;
    & p {
      white-space: nowrap;
      font-weight: ${(p) => p.theme.typography.weights.medium};
      font-size: ${(p) => p.theme.typography.sizes.sm}px;
    }
    & > div {
      padding-right: ${(p) => p.theme.spacings.none}px;
      gap: ${(p) => p.theme.spacings.sm}px;

      & > div:nth-child(2) {
        //Masks vertical bar and padding
        & > div:first-child {
          display: none;
        }
        & > div:nth-child(2) {
          height: 100%;
          border-width: 0;
          &:hover {
            background-color: unset;
          }
        }
      }
    }
  }
`

const MetricWeedgetCondensed: FC<MetricProps> = ({ metric }) => {
  const { t } = useIntl()
  const org = useOrgContext()
  const MetricIcon = MetricIcons[metric]
  const isExpertOrg = org.billingStatus === BillingStatus.expert

  const { id } = useItemContext()
  const client = useClient()
  const deviceRequest = useQuery(client.devices.get(id), { enabled: !isNil(id) })
  const device = deviceRequest.data

  const deviceSummaryRequest = useQuery(
    client.measurements.devices.summaries.getOne({ deviceId: id })
  )
  const summary = deviceSummaryRequest.data?.data

  const { isRestricted } = useIsAppAccessRestricted({
    isVWS: !isNil(device) && isVirtualWeatherStation(device)
  })

  return metric === 'LW_D' ? null : (
    <WeedgetCard $minHeight={'unset'}>
      <DelimitedFlex $alignItems='center' $justifyContent='space-between' $p='md' $gap='md'>
        <Flex $alignItems='center' $gap='md' $overflow='hidden' $pr='md'>
          <MetricIcon
            $size='lg'
            $backgroundColor={`metrics.${metric}.500`}
            $p='sm'
            $color='grayscale.white'
          />
          <Text $flexed $fontWeight='bold' $ellipsis>
            {t(`metrics.${metric}`, { capitalize: true })}
          </Text>
        </Flex>
      </DelimitedFlex>
      {!isRestricted ? (
        <Flex $flexDirection='column' $flex={1}>
          <Flex $gap='lg' $px='md' $pt='md' $pb='sm' $flex={1}>
            {isNil(device) || isNil(summary) ? (
              <Flex $p='md' $flex={1} $justifyContent='center' $alignItems='center'>
                <LoadingCircle size='xl' color='grayscale.black' />
              </Flex>
            ) : (
              <DumbMetricSummaryCondensed
                metric={metric}
                resource={device}
                summary={summary}
                isExpertOrg={isExpertOrg}
              />
            )}
          </Flex>
        </Flex>
      ) : (
        <Flex $flex={1} $p='md'>
          <Flex
            $flex={1}
            $backgroundColor={'grayscale.black'}
            $alignItems='center'
            $justifyContent='center'
            $borderRadius={'md'}
            $flexDirection='column'
            $gap='sm'
            $p='md'
          >
            <Icons.Lock $size='lg' $color={'grayscale.white'} />
            <Text $color='grayscale.white' $fontSize='sm' $textAlign='center'>
              {t('ecommerce.plans.restrictions.carousel_value_downgrade_title')}
            </Text>
          </Flex>
        </Flex>
      )}
    </WeedgetCard>
  )
}

export const MetricWeedget: FC<MetricProps> = ({ metric }) => {
  const { t } = useIntl()
  const client = useClient()
  const { id, timezone } = useItemContext()
  const org = useOrgContext()
  const pathname = usePathname()
  const nav = useNavigate()

  const MetricIcon = MetricIcons[metric]
  const [ref, { height }] = useMeasure<HTMLDivElement>()

  const [, setGlobalHistoryState] = useGlobalHistoryState()

  const options: Array<{
    label: string
    value: OptionValues
    period: Period
  }> = useMemo(
    () => [
      {
        label: t('periods.today', { capitalize: true }),
        value: 'D',
        period: {
          key: 'D',
          span: TimeSteps.perHours,
          since: moment.tz(timezone).startOf('day').unix(),
          before: moment.tz(timezone).startOf('day').add(1, 'day').unix()
        }
      },
      {
        label: t('periods.last_day', { capitalize: true }),
        value: 'DD',
        period: {
          key: 'DD',
          span: TimeSteps.perHours,
          since: moment.tz(timezone).startOf('day').add(-1, 'day').unix(),
          before: moment.tz(timezone).startOf('day').unix()
        }
      },
      {
        label: t('periods.last_7_days', { capitalize: true }),
        value: '7D',
        period: {
          key: '7D',
          span: TimeSteps.perHours,
          since: moment.tz(timezone).startOf('day').add(-6, 'day').unix(),
          before: moment.tz(timezone).startOf('day').add(1, 'day').unix()
        }
      }
    ],
    [t, timezone]
  )

  const [selectedValue, setSelectedValue] = useState<OptionValues>('D')

  const selectedPeriod = useMemo(
    () => options.find((option) => option.value === selectedValue)!.period,
    [options, selectedValue]
  )

  const metricToPrefetch = getMetricsToFetch([metric])

  const deviceMeasurementsRequest = useQuery(
    client.measurements.devices.histories.get1_1({
      deviceId: id,
      ...selectedPeriod,
      metrics: metricToPrefetch
    }),
    {
      enabled: !isNil(selectedPeriod)
    }
  )

  const deviceRequest = useQuery(client.devices.get(id), { enabled: !isNil(id) })
  const device = deviceRequest.data

  const prefetch: UseMetricSummaryChartInterface['prefetch'] = {
    isLoading: deviceMeasurementsRequest.isLoading,
    measurements: deviceMeasurementsRequest.data
  }

  const isExpertOrg = org.billingStatus === BillingStatus.expert

  return (
    <WeedgetCard $position='relative' $flex={1}>
      {/*  HEADER */}
      <WeedgetHeader>
        <Flex $alignItems='center' $flex={1} $minWidth={0} $gap={'md'}>
          <MetricIcon
            $size='lg'
            $backgroundColor={`metrics.${metric}.500`}
            $p='sm'
            $color='grayscale.white'
          />
          <Flex $flexDirection='column' $flex={1} $minWidth={0}>
            <Text $fontWeight='bold' $ellipsis>
              {t(`metrics.${metric}`, { capitalize: true })}
            </Text>
          </Flex>
        </Flex>
        <WeedgetHeaderSelect<OptionValues>
          value={selectedValue}
          name={'period_selector'}
          onChange={(value) => {
            if (isNil(value) || isArray(value)) return
            setSelectedValue(value)
          }}
          options={options}
          label={undefined}
          hideErrors
          className={PREVENT_DRAG_CLASSNAME}
        />
      </WeedgetHeader>

      {/* CONTENT */}
      <Flex $flexDirection='column' $flex={1}>
        {isNil(device) || isNil(prefetch) ? (
          <WeedgetLoading />
        ) : (
          <>
            {/* AGGREGATES */}
            <DelimitedFlex $gap='lg' $px='md' $pt='md' $pb='sm'>
              <PeriodBasedMetricSummary
                resource={device}
                metric={metric}
                prefetch={prefetch}
                period={selectedPeriod}
                isExpertOrg={isExpertOrg}
              />
            </DelimitedFlex>
            {/* CHART */}
            <Flex $flex={1} ref={ref} $flexDirection='column'>
              <MetricChart
                metric={metric}
                prefetch={prefetch}
                height={height}
                variant='sm'
                isVWS={isVirtualWeatherStation(device)}
              />
            </Flex>
          </>
        )}
      </Flex>

      {/* FOOTER */}
      <WeedgetFooter
        destination={() => {
          if (!isNil(device)) {
            const newHistoryState: Partial<HistoryState> = {
              ...getDefaultHistoryState(device.timezone),
              selectedMetrics: [metric],
              since: selectedPeriod.since,
              before: selectedPeriod.before,
              selectedPeriodId: 'custom' as PredefinedPeriodIds,
              selectedTimeStep: selectedPeriod.span
            }
            setGlobalHistoryState(newHistoryState)
          }
          nav(`${pathname}/history`, { replace: true })
        }}
        label={t('dashboards.metrics.see_more', { capitalize: true })}
      />
    </WeedgetCard>
  )
}

export { MetricWeedgetCondensed, MetricWeedget as default }
